import { UI, HStack, Stack, VStack } from '@genial/components';
import { BoxRadius, Spacing, Typography } from '@genial/design-tokens';
import { Pressable } from 'native-base';
import React from 'react';

import { InputMoney } from '../../../../../../../../../components/InputMoney';
import { RiskRating } from '../../../../../../../../../components/RiskRating';
import {
  TableContent,
  TableContentItemCaptions,
} from '../../../../../../../../../components/TableContent';
import { useGlobalAssetContext } from '../../../../../contexts';
import { useFundsAssetContext } from '../../../../../contexts/products/Funds';
import { useFundsAssetController } from '../../../../../hooks/products/Funds';
import { LateralBar } from '../../../components/LateralBar';
import { ListLoadingItemMobile } from '../ListLoadingItem/mobile';

import { FundsListItemTypes } from '.';

export const FundsListItemMobile: React.FC<FundsListItemTypes.Props> = ({ offer }) => {
  const id = offer.productIdNormalized;
  const {
    handleInputFocus,
    handleInputClear,
    isFocused,
    handleInputBlur,
    handleSubmit,
    control,
    hasError,
    helpText,
    currentValueFormatted,
    currentValue,
  } = useFundsAssetController(offer);
  const { setModalOpen, setOfferSelected } = useFundsAssetContext();
  const { infoGetter, selectedTab } = useGlobalAssetContext();

  const completeOffer = { ...offer, orderValue: currentValue };

  const { Button, isSelected, forceLoading } = infoGetter(
    'funds',
    completeOffer,
    'productIdNormalized'
  );

  const onOpenDetailsModal = () => {
    setModalOpen(true);
    setOfferSelected(offer);
  };

  const middleInfos1 = [
    {
      label: 'Tipo',
      value: <UI.Text color="neutral.dark">{offer.productCategoryNormalized}</UI.Text>,
    },
    {
      label: 'Rent. 12 meses',
      value: <UI.Text color="neutral.dark">{offer.profitabilityTwelveMonthsNormalized}</UI.Text>,
    },
  ];

  const middleInfos2 = [
    {
      label: 'Taxa admin.',
      value: <UI.Text color="neutral.dark">{offer.administrationFeeNormalized}</UI.Text>,
      maxW: '50%',
    },
    {
      label: 'Resgate',
      value: (
        <UI.Text color="neutral.dark">{offer.quotationRedemptionTermFormattedNormalized}</UI.Text>
      ),
    },
  ];

  if (forceLoading) {
    return <ListLoadingItemMobile />;
  }

  return (
    <TableContent
      direction="row"
      space={0}
      p={0}
      px={Spacing['inline.xxxs']}
      isHighlighted={isSelected}>
      <LateralBar
        backgroundColor={selectedTab.color}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <VStack padding={Spacing['inset.sm']} space={Spacing['stack.xxxs']} w="full">
        <HStack justifyContent="space-between" alignItems="center">
          <UI.Text variant="semibold" color="neutral.black" fontSize={Typography['fontsize.lg']}>
            {offer.productNameNormalized}
          </UI.Text>
          <Pressable onPress={onOpenDetailsModal}>
            <UI.Icon name="Info" size="xs" />
          </Pressable>
        </HStack>
        <HStack justifyContent="space-between" alignItems="center">
          <TableContentItemCaptions icons={offer.captions} />
          <VStack space={Spacing['stack.quarck']}>
            <UI.Text color="neutral.black" variant="semibold">
              Risco
            </UI.Text>
            <RiskRating
              currentRiskRating={Number(offer.riskRatingLevelNormalized) || 0}
              tooltip={offer.riskRatingNormalized || ''}
            />
          </VStack>
        </HStack>
        <HStack justifyContent="space-between">
          {middleInfos1.map(({ label, value }) => (
            <VStack space={Spacing['stack.quarck']} key={label}>
              <UI.Text color="neutral.black" variant="semibold">
                {label}
              </UI.Text>
              {value}
            </VStack>
          ))}
        </HStack>
        <HStack justifyContent="space-between">
          {middleInfos2.map(({ label, value, maxW }) => (
            <VStack maxW={maxW} space={Spacing['stack.quarck']} key={label}>
              <UI.Text color="neutral.black" variant="semibold">
                {label}
              </UI.Text>
              {value}
            </VStack>
          ))}
        </HStack>
        <HStack space={Spacing['inline.xxxs']} justifyContent="space-between" w="full">
          <VStack space={Spacing['stack.quarck']} w="75%">
            <UI.Text color="neutral.black" variant="semibold">
              Valor a aplicar:
            </UI.Text>
            <InputMoney
              id={`fixedIncome-input${id}`}
              name="createAppliedAmount"
              control={control}
              handleFocus={handleInputFocus}
              handleBlur={handleInputBlur}
              handleSubmit={handleSubmit}
              handleClear={handleInputClear}
              currentValue={currentValueFormatted}
              hasFocus={isFocused}
              minValue={offer.applicationMinimumAmount || 0}
              isDisabled={isSelected}
              customInputProps={{
                ...{ ...(isFocused && hasError && { error: hasError }) },
                ...{ ...(isFocused && !hasError && { helpText }) },
              }}
            />
          </VStack>
          <Stack alignSelf="flex-end">
            <Button
              id={selectedTab.typeGetterId}
              offer={completeOffer}
              isDisabled={typeof hasError?.message === 'string'}
              productContextConstant={selectedTab}
            />
          </Stack>
        </HStack>
      </VStack>
    </TableContent>
  );
};
